/**
 * Componente para mostrar imágenes/documentos que pueden estar encriptados.
 * Compatible con archivos legacy (sin encriptar) y nuevos (encriptados AES-256-GCM).
 */

import { Loader2, FileText, AlertCircle, ExternalLink } from "lucide-react";
import { useDecryptedUrl } from "@/hooks/useDecryptedUrl";
import type { EncryptionMeta } from "@/lib/services/crypto";

interface EncryptedDocumentViewProps {
  /** URL del archivo (encriptado o legacy) */
  url: string | null | undefined;
  /** Metadata de encriptación. null/undefined = archivo legacy */
  encryption?: EncryptionMeta | null;
  /** Content-type original */
  contentType?: string;
  /** Alt text para imagen */
  alt?: string;
  /** Clases CSS para la imagen */
  className?: string;
  /** Si es un PDF (para mostrar icono en lugar de imagen) */
  isPdf?: boolean;
  /** Callback de error al cargar imagen */
  onError?: (e: React.SyntheticEvent<HTMLImageElement>) => void;
}

export function EncryptedDocumentView({
  url,
  encryption,
  contentType,
  alt = "Documento",
  className = "w-full h-full object-cover",
  isPdf = false,
  onError,
}: EncryptedDocumentViewProps) {
  const { decryptedUrl, isDecrypting, error } = useDecryptedUrl({
    url,
    encryption: encryption ?? null,
    contentType: contentType || (isPdf ? "application/pdf" : "image/jpeg"),
  });

  if (!url) return null;

  if (isDecrypting) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-4">
        <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
        <span className="text-xs text-gray-500">Desencriptando...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-4 text-red-500">
        <AlertCircle className="w-6 h-6" />
        <span className="text-xs">{error}</span>
      </div>
    );
  }

  if (!decryptedUrl) return null;

  if (isPdf) {
    return (
      <a
        href={decryptedUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="flex flex-col items-center gap-2 text-primary-600 hover:text-primary-700 p-4"
      >
        <FileText className="w-10 h-10 text-red-600" />
        <span className="text-xs font-medium">Ver PDF</span>
        <ExternalLink className="w-3 h-3" />
      </a>
    );
  }

  return (
    <img src={decryptedUrl} alt={alt} className={className} onError={onError} />
  );
}

export default EncryptedDocumentView;
